import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { catalogAPI, projectsAPI } from '../services/api';
import { 
  FileSpreadsheet, 
  Plus, 
  Search, 
  Filter,
  Pencil,
  Trash2,
  Download,
  Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const Catalogs = () => {
  const [searchParams] = useSearchParams();
  const { isDeveloper } = useAuth();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(searchParams.get('project') || '');
  const [catalog, setCatalog] = useState([]);
  const [filteredCatalog, setFilteredCatalog] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    unit: '',
    quantity: '',
    unit_price: '',
    category: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchCatalog();
    }
  }, [selectedProject]);

  useEffect(() => {
    filterCatalog();
  }, [searchTerm, categoryFilter, catalog]);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      const projects = response?.data?.data || response?.data || [];
      setProjects(projects);
      if (projects.length > 0 && !selectedProject) {
        setSelectedProject(projects[0].id);
      }
    } catch (error) {
      console.error('Error al cargar proyectos:', error);
      toast.error('Error al cargar proyectos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalog = async () => {
    try {
      const response = await catalogAPI.getByProject(selectedProject);
      const catalogData = response?.data?.data || response?.data;
      const catalog = Array.isArray(catalogData) ? catalogData : [];
      setCatalog(catalog);
      
      const uniqueCategories = Array.isArray(catalog) 
        ? [...new Set(catalog.map(item => item.category).filter(Boolean))]
        : [];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error al cargar catálogo:', error);
      toast.error('Error al cargar catálogo');
      setCatalog([]);
    }
  };

  const filterCatalog = () => {
    let filtered = [...catalog];
    
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }
    
    setFilteredCatalog(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        project_id: selectedProject,
        quantity: parseFloat(formData.quantity) || 0,
        unit_price: parseFloat(formData.unit_price) || 0
      };

      if (editingItem) {
        await catalogAPI.update(editingItem.id, data);
        toast.success('Partida actualizada');
      } else {
        await catalogAPI.create(data);
        toast.success('Partida creada');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchCatalog();
    } catch (error) {
      toast.error('Error al guardar partida');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      code: item.code,
      description: item.description,
      unit: item.unit,
      quantity: item.quantity.toString(),
      unit_price: item.unit_price.toString(),
      category: item.category || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await catalogAPI.delete(deleteId);
      toast.success('Partida eliminada');
      fetchCatalog();
    } catch (error) {
      toast.error('Error al eliminar partida');
    } finally {
      setDeleteId(null);
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      code: '',
      description: '',
      unit: '',
      quantity: '',
      unit_price: '',
      category: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const totalBudget = filteredCatalog.reduce((sum, item) => sum + (item.total_price || 0), 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="catalogs-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#2D2A26]">Catálogos</h1>
            <p className="text-[#5C554B]">Gestiona las partidas de cada obra</p>
          </div>
          {isDeveloper() && selectedProject && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-item-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  Nueva Partida
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingItem ? 'Editar Partida' : 'Nueva Partida'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="code">Código *</Label>
                      <Input
                        id="code"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                        placeholder="P001"
                        className="input-default"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Categoría</Label>
                      <Input
                        id="category"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        placeholder="Cimentación"
                        className="input-default"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="description">Descripción *</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Descripción de la partida"
                      className="input-default"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="unit">Unidad *</Label>
                      <Input
                        id="unit"
                        value={formData.unit}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                        placeholder="m2"
                        className="input-default"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="quantity">Cantidad *</Label>
                      <Input
                        id="quantity"
                        type="number"
                        step="0.01"
                        value={formData.quantity}
                        onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                        placeholder="100"
                        className="input-default"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="unit_price">P.U. *</Label>
                      <Input
                        id="unit_price"
                        type="number"
                        step="0.01"
                        value={formData.unit_price}
                        onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                        placeholder="500"
                        className="input-default"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="btn-primary">
                      {editingItem ? 'Actualizar' : 'Crear'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Project Selector */}
        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Label className="mb-2 block">Seleccionar Obra</Label>
                <Select value={selectedProject} onValueChange={setSelectedProject}>
                  <SelectTrigger className="h-12 bg-white">
                    <SelectValue placeholder="Selecciona una obra" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.isArray(projects) && projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="mb-2 block">Buscar</Label>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                  <Input
                    placeholder="Código o descripción..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input-default pl-12"
                  />
                </div>
              </div>
              {categories.length > 0 && (
                <div className="sm:w-48">
                  <Label className="mb-2 block">Categoría</Label>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="h-12 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Catalog Table */}
        {selectedProject && (
          <Card className="card-dashboard">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Partidas del Catálogo</CardTitle>
                <p className="text-sm text-[#5C554B] mt-1">
                  {filteredCatalog.length} partidas | Total: {formatCurrency(totalBudget)}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              {!Array.isArray(filteredCatalog) || filteredCatalog.length === 0 ? (
                <div className="text-center py-12">
                  <FileSpreadsheet className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-[#2D2A26] mb-2">
                    {searchTerm || categoryFilter !== 'all' ? 'No se encontraron partidas' : 'Catálogo vacío'}
                  </h3>
                  <p className="text-[#5C554B]">
                    {searchTerm || categoryFilter !== 'all' ? 'Intenta con otros filtros' : 'Agrega la primera partida'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Descripción</TableHead>
                        <TableHead>Categoría</TableHead>
                        <TableHead className="text-right">Cantidad</TableHead>
                        <TableHead className="text-right">P.U.</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Ejecutado</TableHead>
                        {isDeveloper() && <TableHead className="w-20"></TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Array.isArray(filteredCatalog) && filteredCatalog.map((item) => {
                        const progressPercent = item.quantity > 0 
                          ? Math.round((item.executed_quantity / item.quantity) * 100) 
                          : 0;
                        return (
                          <TableRow key={item.id}>
                            <TableCell className="font-mono text-sm font-medium">{item.code}</TableCell>
                            <TableCell className="max-w-xs truncate">{item.description}</TableCell>
                            <TableCell>
                              {item.category && (
                                <span className="px-2 py-1 bg-[#EBE5DA] text-[#5C554B] rounded text-xs">
                                  {item.category}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">{item.quantity} {item.unit}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(item.total_price)}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <span className={progressPercent > 0 ? 'text-[#81A183]' : 'text-[#9CA3AF]'}>
                                  {item.executed_quantity} {item.unit}
                                </span>
                                <span className="text-xs text-[#9CA3AF]">({progressPercent}%)</span>
                              </div>
                            </TableCell>
                            {isDeveloper() && (
                              <TableCell>
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleEdit(item)}
                                    className="h-8 w-8"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setDeleteId(item.id)}
                                    className="h-8 w-8 text-red-600 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta partida?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default Catalogs;
